import { CategoryApi } from '../api';
import { openDialog } from '@/utils/utils';

/**
 * 카테고리 서비스 클래스
 * API 호출과 데이터 처리 로직을 분리하여 관리합니다.
 */
export default class CategoryService {
  /**
   * 카테고리 목록 조회
   * @param {Object} params - 검색 파라미터
   * @returns {Promise<{items: Array, total: number}>} 카테고리 목록과 총 개수
   */
  static async getCategoryList(params) {
    try {
      const response = await CategoryApi.getCategoryList(params);
      
      if (response.data) {
        return {
          items: response.data.items || [],
          total: response.data.total || 0,
          totalPages: Math.ceil((response.data.total || 0) / (params.limit || 10))
        };
      }
      
      return { items: [], total: 0, totalPages: 0 };
    } catch (error) {
      this.handleApiError(error, '카테고리 목록을 불러오는 중 오류가 발생했습니다.');
      return { items: [], total: 0, totalPages: 0 };
    }
  }
  
  /**
   * 카테고리 상세 조회
   * @param {string} categoryId - 카테고리 ID
   * @returns {Promise<Object>} 카테고리 상세 정보
   */
  static async getCategory(categoryId) {
    try {
      const response = await CategoryApi.getCategory(categoryId);
      return response.data;
    } catch (error) {
      this.handleApiError(error, '카테고리 정보를 불러오는 중 오류가 발생했습니다.');
      return null;
    }
  }
  
  /**
   * 카테고리 생성
   * @param {Object} categoryData - 카테고리 데이터
   * @returns {Promise<boolean>} 성공 여부
   */
  static async createCategory(categoryData) {
    try {
      await CategoryApi.createCategory(categoryData);
      await openDialog('카테고리가 등록되었습니다.', 'success', '확인');
      return true;
    } catch (error) {
      this.handleApiError(error, '카테고리 등록 중 오류가 발생했습니다.');
      return false;
    }
  }
  
  /**
   * 카테고리 수정
   * @param {string} categoryId - 카테고리 ID
   * @param {Object} categoryData - 카테고리 데이터
   * @returns {Promise<boolean>} 성공 여부
   */
  static async updateCategory(categoryId, categoryData) {
    try {
      await CategoryApi.updateCategory(categoryId, categoryData);
      await openDialog('카테고리가 수정되었습니다.', 'success', '확인');
      return true;
    } catch (error) {
      this.handleApiError(error, '카테고리 수정 중 오류가 발생했습니다.');
      return false;
    }
  }
  
  /**
   * 카테고리 삭제
   * @param {string} categoryId - 카테고리 ID
   * @returns {Promise<boolean>} 성공 여부
   */
  static async deleteCategory(categoryId) {
    try {
      await CategoryApi.deleteCategory(categoryId);
      await openDialog('카테고리가 삭제되었습니다.', 'success', '확인');
      return true;
    } catch (error) {
      this.handleApiError(error, '카테고리 삭제 중 오류가 발생했습니다.');
      return false;
    }
  }
  
  /**
   * 여러 카테고리 순서 일괄 변경
   * @param {Array<Object>} orderDataList - 순서 변경 데이터 배열
   * @returns {Promise<boolean>} 성공 여부
   */
  static async updateCategoriesOrder(orderDataList) {
    try {
      await CategoryApi.updateCategoriesOrder(orderDataList);
      return true;
    } catch (error) {
      this.handleApiError(error, '카테고리 순서 일괄 변경 중 오류가 발생했습니다.');
      return false;
    }
  }
  
  /**
   * API 오류 처리
   * @param {Error} error - 에러 객체
   * @param {string} message - 오류 메시지
   * @private
   */
  static async handleApiError(error, message) {
    console.error(message, error);
    
    // 서버 에러 메시지가 있으면 해당 메시지 사용
    const errorMessage = error.response?.data?.message || message;
    await openDialog(errorMessage, 'error', '확인');
  }
} 